import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { getAnnouncementDetails, approveAnnouncement, rejectAnnouncement } from "proxyUrl";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./announcements.css";
import { useSelector } from "react-redux";

const AnnouncementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [announcementData, setAnnouncementData] = useState(null);
  const [error, setError] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canViewAnnouncement = userPermissions.includes("view announcement") || userPermissions.includes("view all announcement");
  const canUpdateAnnouncement = userPermissions.includes("update announcement");
  const canDeleteAnnouncement = userPermissions.includes("delete announcement");

  useEffect(() => {
    fetchAnnouncementDetails();
  }, [id]);

  const fetchAnnouncementDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${getAnnouncementDetails}${id}/details`, {
        withCredentials: true,
      });

      if (response.data && response.data.data) {
        setAnnouncementData(response.data.data);
        setCanApprove(response.data.canApprove || false);
      }
    } catch (error) {
      console.error("Error fetching announcement details:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to fetch announcement details";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this announcement?")) {
      return;
    }
    setProcessingAction(true);
    try {
      const response = await axios.post(
        `${approveAnnouncement}${id}/approve`,
        {},
        { withCredentials: true }
      );
      if (response.status === 200) {
        toast.success("Announcement approved successfully!", {
          position: "top-center",
          autoClose: 2000,
        });
        setTimeout(() => navigate("/admin/announcements"), 2100);
      }
    } catch (error) {
      console.error("Error approving announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to approve announcement!",
        {
          position: "top-center",
          autoClose: 2000,
        }
      );
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    const rejectionReason = prompt("Please enter rejection reason:");
    if (!rejectionReason || rejectionReason.trim() === "") {
      toast.error("Rejection reason is required!", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    setProcessingAction(true);
    try {
      const response = await axios.post(
        `${rejectAnnouncement}${id}/reject`,
        { rejectionReason: rejectionReason.trim() },
        { withCredentials: true }
      );
      if (response.status === 200) {
        toast.success("Announcement rejected successfully!", {
          position: "top-center",
          autoClose: 2000,
        });
        setTimeout(() => navigate("/admin/announcements"), 2100);
      }
    } catch (error) {
      console.error("Error rejecting announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to reject announcement!",
        {
          position: "top-center",
          autoClose: 2000,
        }
      );
      setProcessingAction(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
    };
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          statusColors[status] || statusColors.pending
        }`}
      >
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getNotificationLabel = (notification) => {
    const labels = {
      0: "All",
      1: "VL",
      2: "Vnaya",
      3: "Creed",
      4: "ASI",
    };
    return labels[notification] || "All";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[83vh]">
        <div className="text-gray-500 text-lg">Loading announcement details...</div>
      </div>
    );
  }

  if (!canViewAnnouncement) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        You are not authorized to view announcements.
      </div>
    );
  }

  if (error || !announcementData) {
    return (
      <div className="mx-4 mt-6">
        <div className="rounded-2xl bg-red-50 dark:bg-red-900/20 p-6 shadow-lg">
          <div className="text-xl font-bold text-red-800 dark:text-red-300 mb-4">
            Error
          </div>
          <div className="text-red-600 dark:text-red-400 mb-4">
            {error || "Announcement not found"}
          </div>
          <Link
            to="/admin/announcements"
            className="inline-block rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
          >
            Back to Announcements
          </Link>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <>
      <div className="mx-4 mt-4">
        {/* Header */}
        <div className="add-booking-header relative mb-4 flex items-center mt-8 justify-between rounded-2xl bg-indigo-600 p-3 shadow">
          <div className="text-lg font-semibold text-white">Announcement Details</div>
          <Link
            to="/admin/announcements"
            className="rounded-full bg-white px-3 py-1 text-sm font-medium text-indigo-600 shadow-sm transition hover:-translate-y-px hover:shadow"
          >
            Back to List
          </Link>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Left Column - Main Content */}
          <div className="space-y-4 lg:col-span-2">
            {/* Image */}
            {announcementData.image && (
              <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-navy-800">
                <img
                  src={announcementData.image}
                  alt={announcementData.title}
                  className="h-auto w-full rounded-lg object-cover"
                />
              </div>
            )}

            {/* Title and Description */}
            <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
              <h1 className="mb-3 text-2xl font-semibold text-gray-800 dark:text-white">
                {announcementData.title}
              </h1>
              <div
                className="prose max-w-none text-sm leading-relaxed text-gray-700 dark:text-gray-300"
                dangerouslySetInnerHTML={{
                  __html: announcementData.description || "",
                }}
              />
            </div>

            {/* Comments Section */}
            {announcementData.comments && announcementData.comments.length > 0 && (
              <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Comments ({announcementData.comments.length})
                  </h2>
                </div>
                <div className="space-y-3">
                  {announcementData.comments.map((comment, index) => (
                    <div
                      key={comment.id || index}
                      className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-white/5"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600/10 text-sm font-semibold text-indigo-600">
                          {(comment.name || "A").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-gray-800 dark:text-white">
                              {comment.name || "Anonymous"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {comment.createdAt
                                ? new Date(comment.createdAt).toLocaleString("en-US")
                                : ""}
                            </div>
                          </div>
                          {comment.email && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {comment.email}
                            </div>
                          )}
                          <div className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                            {comment.comment}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-4">
            {/* Status and Info Card */}
            <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-gray-800 dark:text-white">Information</h2>
                <div className="flex items-center gap-2">
                  {canUpdateAnnouncement && (
                    <Link
                      to={`/admin/announcements/edit/${id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-navy-700"
                      title="Edit announcement"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125L16.862 4.487" />
                      </svg>
                      Edit
                    </Link>
                  )}
                  {canDeleteAnnouncement && (
                    <Link
                      to={`/admin/announcements/delete/${id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
                      title="Delete announcement"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M10 11v6m4-6v6m-7 3h10a1 1 0 001-1V7H5v12a1 1 0 001 1zm9-16V4a1 1 0 00-1-1H9a1 1 0 00-1 1v1h10z" />
                      </svg>
                      Delete
                    </Link>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-gray-500">Status</label>
                  <div className="mt-1 text-sm">{getStatusBadge(announcementData.status)}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Type</label>
                  <div className="mt-1 text-sm text-gray-800 dark:text-white">
                    {announcementData.announcementType || "General"}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Notification</label>
                  <div className="mt-1 text-sm text-gray-800 dark:text-white">
                    {getNotificationLabel(announcementData.notification)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Created</label>
                  <div className="mt-1 text-sm text-gray-800 dark:text-white">
                    {announcementData.createdAt
                      ? new Date(announcementData.createdAt).toLocaleString("en-US")
                      : "-"}
                  </div>
                </div>
                {announcementData.updatedAt && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Updated</label>
                    <div className="mt-1 text-sm text-gray-800 dark:text-white">
                      {new Date(announcementData.updatedAt).toLocaleString("en-US")}
                    </div>
                  </div>
                )}
                {/* {announcementData.updatedAt && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Updated</label>
                    <div className="mt-1 text-sm text-gray-800 dark:text-white">
                      {new Date(announcementData.updatedAt).toLocaleString("en-US")}
                    </div>
                  </div>
                )} */}
                {announcementData.approver && (
                  <div className="rounded-xl bg-white shadow-sm dark:bg-navy-800">
                    <h2 className="text-xs font-medium text-gray-500">Approved By</h2>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {announcementData.approver.name || "Unknown"}
                      </div>
                      <div className="text-xs text-gray-500">{announcementData.approver.email || ""}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Engagement Card */}
            <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
              <h2 className="mb-3 text-base font-semibold text-gray-800 dark:text-white">Engagement</h2>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex justify-between">
                  <span>Likes</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {announcementData.likeCount || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Comments</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {announcementData.comments?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Creator Card */}
            {announcementData.creator && (
              <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
                <h2 className="mb-3 text-base font-semibold text-gray-800 dark:text-white">Created By</h2>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {announcementData.creator.name || "Unknown"}
                  </div>
                  <div className="text-xs text-gray-500">{announcementData.creator.email || ""}</div>
                </div>
              </div>
            )}

            {/* Approver Card */}
            

            {/* Actions */}
            {/* <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
              <h2 className="mb-3 text-base font-semibold text-gray-800 dark:text-white">Actions</h2>
              <div className="flex flex-col gap-2">
                {canApprove && announcementData.status === "pending" ? (
                  processingAction ? (
                    <div className="w-full rounded-md bg-gray-300 px-4 py-2 text-center text-sm text-white">
                      Processing...
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleApprove}
                        className="w-full rounded-md bg-green-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-600"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={handleReject}
                        className="w-full rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                      >
                        Reject
                      </button>
                    </>
                  )
                ) : null}
                <Link
                  to={`/admin/announcements/edit/${id}`}
                  className="w-full rounded-md border border-gray-200 px-4 py-2 text-center text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-navy-700"
                >
                  Edit
                </Link>
                <Link
                  to={`/admin/announcements/delete/${id}`}
                  className="w-full rounded-md border border-gray-200 px-4 py-2 text-center text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-navy-700"
                >
                  Delete
                </Link>
              </div>
            </div> */}
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default AnnouncementDetail;

